import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from "react-router-dom";
import { format } from 'date-fns';
import { it, enUS } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { clientsApi, packagesApi, lessonsApi, trainingPlansApi } from "@/api";
import Header from "@/components/Header";
import LessonModal from "@/components/LessonModal";

const ClientDetailScreen = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [packages, setPackages] = useState([]);
  const [packageOptions, setPackageOptions] = useState([]);
  const [trainingPlans, setTrainingPlans] = useState([]);
  const [expiredPlans, setExpiredPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [showTrainingPlanModal, setShowTrainingPlanModal] = useState(false);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedLesson, setSelectedLesson] = useState(null);

  const currentLocale = i18n.language?.startsWith('it') ? it : enUS;

  const fetchData = useCallback(async () => {
    try {
      const [clientRes, lessonsRes, packagesRes, optionsRes, plansRes, expiredRes] = await Promise.all([
        clientsApi.getOne(id),
        lessonsApi.getAll({ client_id: id }),
        packagesApi.getClientPackages(id),
        packagesApi.getOptions(),
        trainingPlansApi.getByClient(id),
        trainingPlansApi.getExpired(id),
      ]);
      setClient(clientRes.data);
      setLessons(lessonsRes.data);
      setPackages(packagesRes.data);
      setPackageOptions(optionsRes.data);
      setTrainingPlans(plansRes.data);
      setExpiredPlans(expiredRes.data);
    } catch (error) {
      // Silently handle errors in production
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleUpdateLesson = async (lessonId, data) => {
    await lessonsApi.update(lessonId, data);
    setSelectedLesson(null);
    fetchData();
  };

  const handleDeleteLesson = async (lessonId) => {
    await lessonsApi.delete(lessonId);
    fetchData();
  };

  const handleCompleteLesson = async (lessonId) => {
    await lessonsApi.complete(lessonId);
    fetchData();
  };

  const handleCancelLesson = async (lessonId) => {
    await lessonsApi.cancel(lessonId);
    fetchData();
  };

  const handleCreateTrainingPlan = async (weeks) => {
    setPurchaseLoading(true);
    try {
      await trainingPlansApi.create({ client_id: id, weeks });
      setShowTrainingPlanModal(false);
      setSuccessMessage(t('trainingPlans.planCreated', { weeks }));
      fetchData();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      alert(error.response?.data?.detail || t('common.creationError'));
    } finally {
      setPurchaseLoading(false);
    }
  };

  const handleAcknowledgeExpired = async (planId) => {
    try {
      await trainingPlansApi.acknowledge(planId);
      fetchData();
    } catch (error) {
      // Silently handle errors in production
    }
  };

  const handleDeletePlan = async (planId) => {
    try {
      await trainingPlansApi.delete(planId);
      fetchData();
    } catch (error) {
      alert(t('common.deletionError'));
    }
  };

  const handlePurchasePackage = async (pkgType) => {
    setPurchaseLoading(true);
    try {
      await packagesApi.purchase(id, pkgType);
      setShowPackageModal(false);
      setSuccessMessage(t('packages.packagePurchased'));
      fetchData();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      alert(error.response?.data?.detail || t('common.purchaseError'));
    } finally {
      setPurchaseLoading(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm(t('clients.deleteConfirm'))) {
      try {
        await clientsApi.delete(id);
        navigate('/');
      } catch (error) {
        alert(t('common.error'));
      }
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center">{t('common.loading')}</div>;
  if (!client) return <div className="min-h-screen flex items-center justify-center">{t('common.noResults')}</div>;

  const availablePackages = [
    { type: 'trial', name: t('packages.trialLesson'), price: 0, icon: '🎁' },
    { type: 'single', name: `1 ${t('packages.singleLesson')}`, price: 50, icon: '💪' },
    { type: 'three', name: `3 ${t('common.lessons')}`, price: 120, icon: '⭐' },
    { type: 'five', name: `5 ${t('common.lessons')}`, price: 175, icon: '🏆' },
    { type: 'ten', name: `10 ${t('common.lessons')}`, price: 300, icon: '🥇' },
  ];

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 pb-20 md:pt-16">
      <Header title={t('clients.clientDetail')} showBack />
      
      {successMessage && (
        <div className="fixed top-20 left-4 right-4 bg-green-500 text-white p-4 rounded-xl shadow-lg z-50 text-center font-semibold">
          {successMessage}
        </div>
      )}

      {showPackageModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-t-3xl w-full max-w-lg p-6 pb-24 animate-slide-up max-h-[85vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">{t('packages.choosePackage')}</h3>
              <button 
                onClick={() => setShowPackageModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-2xl"
              >
                ✕
              </button>
            </div>
            <div className="space-y-3 overflow-y-auto flex-1 pb-4">
              {availablePackages.map((pkg) => (
                <button
                  key={pkg.type}
                  onClick={() => handlePurchasePackage(pkg.type)}
                  disabled={purchaseLoading}
                  className="w-full flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-xl hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition-colors disabled:opacity-50"
                  data-testid={`select-pkg-${pkg.type}`}
                >
                  <span className="text-3xl">{pkg.icon}</span>
                  <div className="flex-1 text-left">
                    <div className="font-semibold text-gray-900 dark:text-white">{pkg.name}</div>
                  </div>
                  <div className="text-lg font-bold text-indigo-600 dark:text-indigo-400">
                    {pkg.price === 0 ? t('common.free') : `€${pkg.price}`}
                  </div>
                </button>
              ))}
            </div>
            {purchaseLoading && (
              <div className="text-center py-4 text-indigo-600 dark:text-indigo-400 font-medium">
                {t('common.purchasing')}
              </div>
            )}
          </div>
        </div>
      )}
      
      <div className="max-w-lg mx-auto p-4 space-y-4">
        <div className="bg-white rounded-xl shadow-sm p-6 text-center">
          <div className="w-20 h-20 bg-indigo-600 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-3">
            {client.first_name[0]}{client.last_name[0]}
          </div>
          <h2 className="text-xl font-bold text-gray-900">{client.first_name} {client.last_name}</h2>
          
          <div className="mt-4 space-y-2">
            <a href={`tel:${client.phone}`} className="flex items-center justify-center gap-2 p-3 bg-gray-50 rounded-lg hover:bg-gray-100">
              <span>📞</span> {client.phone}
            </a>
            <a href={`mailto:${client.email}`} className="flex items-center justify-center gap-2 p-3 bg-gray-50 rounded-lg hover:bg-gray-100">
              <span>📧</span> {client.email}
            </a>
          </div>

          <div className="mt-4 bg-indigo-50 rounded-xl p-4">
            <span className="text-3xl">💪</span>
            <div className="text-3xl font-bold text-indigo-600">{client.remaining_lessons}</div>
            <div className="text-sm text-gray-600">{t('clients.remainingLessons')}</div>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex gap-3">
            <button
              onClick={() => navigate(`/prenota/${id}`)}
              className="flex-1 bg-indigo-600 text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-indigo-700"
              data-testid="book-lesson-btn"
            >
              + {t('clients.bookLesson')}
            </button>
            <button
              onClick={() => setShowPackageModal(true)}
              className="flex-1 bg-indigo-100 text-indigo-700 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-indigo-200"
              data-testid="buy-package-btn"
            >
              {t('packages.title')}
            </button>
          </div>
          <button
            onClick={() => setShowTrainingPlanModal(true)}
            className="w-full bg-emerald-600 text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-emerald-700"
            data-testid="create-training-plan-btn"
          >
            {t('trainingPlans.create')}
          </button>
        </div>

        {expiredPlans.length > 0 && (
          <div className="space-y-2">
            {expiredPlans.map((plan) => (
              <div key={plan.id} className="bg-red-50 border-2 border-red-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">⚠️</span>
                  <div className="flex-1">
                    <h4 className="font-bold text-red-800">{t('trainingPlans.expiredAlert')}</h4>
                    <p className="text-sm text-red-600">
                      {t('trainingPlans.expiredOn', { weeks: plan.weeks, date: format(new Date(plan.end_date), 'd MMMM yyyy', { locale: currentLocale }) })}
                    </p>
                  </div>
                  <button
                    onClick={() => handleAcknowledgeExpired(plan.id)}
                    className="text-red-600 hover:text-red-800 text-xl"
                    title={t('common.close')}
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div>
          <h3 className="font-bold text-gray-900 mb-3">{t('common.lessons')}</h3>
          {lessons.length === 0 ? (
            <div className="text-center py-6 bg-gray-50 rounded-xl">
              <div className="text-3xl mb-2">📅</div>
              <p className="text-gray-700 font-medium mb-1">{t('lessons.noLessonsScheduled')}</p>
              <p className="text-gray-400 text-sm">{t('lessons.bookFirstLesson')}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {lessons.map((lesson) => {
                const statusStyle = lesson.status === 'scheduled' ? 'bg-indigo-100 text-indigo-700' :
                                   lesson.status === 'completed' ? 'bg-green-100 text-green-700' :
                                   'bg-red-100 text-red-700';
                const statusText = lesson.status === 'scheduled' ? t('lessons.status.scheduled') :
                                  lesson.status === 'completed' ? t('lessons.status.completed') : t('lessons.status.cancelled');
                return (
                  <div 
                    key={lesson.id} 
                    className="bg-white rounded-xl shadow-sm p-4 cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => setSelectedLesson({ ...lesson, client_name: `${client.first_name} ${client.last_name}` })}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-semibold capitalize">
                          {format(new Date(lesson.date), 'EEEE d MMMM', { locale: currentLocale })}
                        </div>
                        <div className="text-sm text-gray-500">{lesson.start_time} - {lesson.end_time}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusStyle}`}>
                          {statusText}
                        </span>
                        <span className="text-gray-400">&rarr;</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div>
          <h3 className="font-bold text-gray-900 mb-3">{t('clients.activePackages')}</h3>
          {packages.length === 0 ? (
            <div className="text-center py-6 bg-gray-50 rounded-xl">
              <div className="text-3xl mb-2">📦</div>
              <p className="text-gray-700 font-medium mb-1">{t('packages.noActivePackages')}</p>
              <p className="text-gray-400 text-sm">{t('packages.addPackageFor')}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {packages.map((pkg) => (
                <div key={pkg.id} className="bg-white rounded-xl shadow-sm p-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-semibold">
                      {packageOptions.find(o => o.type === pkg.package_type)?.name || pkg.package_type}
                    </span>
                    <span className="text-indigo-600 font-bold">
                      {pkg.price === 0 ? t('common.free') : `€${pkg.price}`}
                    </span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-indigo-600 rounded-full transition-all"
                      style={{ width: `${(pkg.lessons_remaining / pkg.lessons_total) * 100}%` }}
                    />
                  </div>
                  <div className="text-sm text-gray-500 mt-1">
                    {pkg.lessons_remaining}/{pkg.lessons_total} {t('clients.lessonsRemaining')}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <h3 className="font-bold text-gray-900 mb-3">{t('trainingPlans.title')}</h3>
          {trainingPlans.length === 0 ? (
            <p className="text-center text-gray-500 py-4">{t('trainingPlans.noPlans')}</p>
          ) : (
            <div className="space-y-2">
              {trainingPlans.map((plan) => {
                const isExpired = new Date(plan.end_date) < new Date();
                const daysLeft = Math.ceil((new Date(plan.end_date) - new Date()) / (1000 * 60 * 60 * 24));
                return (
                  <div 
                    key={plan.id} 
                    className={`bg-white rounded-xl shadow-sm p-4 ${isExpired ? 'border-2 border-red-200' : ''}`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <span className="font-semibold">{t('trainingPlans.plan')} {plan.weeks} {t('common.weeks')}</span>
                        <div className="text-sm text-gray-500 mt-1">
                          {format(new Date(plan.start_date), 'd MMM', { locale: currentLocale })} - {format(new Date(plan.end_date), 'd MMM yyyy', { locale: currentLocale })}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {isExpired ? (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                            {t('trainingPlans.expired')}
                          </span>
                        ) : (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                            {daysLeft} {t('common.daysLeft')}
                          </span>
                        )}
                        <button
                          onClick={() => handleDeletePlan(plan.id)}
                          className="text-gray-400 hover:text-red-500"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                    {!isExpired && (
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-emerald-500 rounded-full transition-all"
                          style={{ width: `${Math.max(0, (daysLeft / (plan.weeks * 7)) * 100)}%` }}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <button
          onClick={handleDelete}
          className="w-full bg-red-50 text-red-600 py-3 rounded-xl font-semibold hover:bg-red-100"
          data-testid="delete-client-btn"
        >
          {t('clients.deleteClient')}
        </button>
      </div>

      {showTrainingPlanModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">{t('trainingPlans.create')}</h3>
              <button 
                onClick={() => setShowTrainingPlanModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ✕
              </button>
            </div>
            <p className="text-gray-600 mb-4">
              {t('trainingPlans.weeksQuestion')}
            </p>
            <div className="grid grid-cols-2 gap-3 mb-6">
              {[4, 6, 8, 12].map((weeks) => (
                <button
                  key={weeks}
                  onClick={() => handleCreateTrainingPlan(weeks)}
                  disabled={purchaseLoading}
                  className="p-4 bg-emerald-50 rounded-xl hover:bg-emerald-100 transition-colors disabled:opacity-50"
                  data-testid={`training-plan-${weeks}-weeks`}
                >
                  <div className="text-2xl font-bold text-emerald-600">{weeks}</div>
                  <div className="text-sm text-gray-600">{t('common.weeks')}</div>
                </button>
              ))}
            </div>
            <div className="border-t pt-4">
              <p className="text-sm text-gray-500 text-center">
                {t('trainingPlans.expiryReminder')}
              </p>
            </div>
            {purchaseLoading && (
              <div className="text-center py-2 text-emerald-600 font-medium">
                {t('common.creating')}
              </div>
            )}
          </div>
        </div>
      )}

      {selectedLesson && (
        <LessonModal
          lesson={selectedLesson}
          onClose={() => setSelectedLesson(null)}
          onUpdate={handleUpdateLesson}
          onDelete={handleDeleteLesson}
          onComplete={handleCompleteLesson}
          onCancel={handleCancelLesson}
        />
      )}
    </div>
  );
};

export default ClientDetailScreen;
